/**
 * Public Accept-Invite page. The Owner sends an invite → the invitee opens
 * the link → this page validates the token, asks them to set a password,
 * then logs them straight in.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Lock, ShieldCheck } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import api from '../lib/api';

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking | ready | error | submitting
  const [invite, setInvite] = useState(null);
  const [errMsg, setErrMsg] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  useEffect(() => {
    api.get(`/auth/invite/${encodeURIComponent(token)}`)
      .then((r) => { setInvite(r.data); setStatus('ready'); })
      .catch((e) => { setErrMsg(e.response?.data?.detail || 'Invite not found or expired'); setStatus('error'); });
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setErrMsg('');
    if (password.length < 10) { setErrMsg('Password must be at least 10 characters.'); return; }
    if (password !== confirm) { setErrMsg('Passwords don\u2019t match.'); return; }
    setStatus('submitting');
    try {
      const r = await api.post(`/auth/invite/${encodeURIComponent(token)}/accept`, { password });
      localStorage.setItem('gl_admin_token', r.data.token);
      localStorage.setItem('gl_admin_email', r.data.email);
      localStorage.setItem('gl_admin_role', r.data.role);
      navigate('/admin');
    } catch (e2) {
      setErrMsg(e2.response?.data?.detail || 'Could not accept invite');
      setStatus('ready');
    }
  }

  return (
    <div className="min-h-screen bg-admin flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl border border-charcoal/10 max-w-md w-full p-8 shadow-sm">
        <Link to="/" className="inline-flex"><BrandLogo size="md" /></Link>
        <div className="label text-charcoal/50 mt-1 mb-6">Admin invite</div>

        {status === 'checking' && <div className="text-sm text-muted" data-testid="invite-checking">Checking invite…</div>}

        {status === 'error' && (
          <div data-testid="invite-error">
            <div className="font-display text-2xl mb-2">Invite unavailable</div>
            <p className="text-muted text-sm mb-6">{errMsg}</p>
            <Link to="/" className="text-heritage text-sm underline">Back to GoLeicestershire</Link>
          </div>
        )}

        {(status === 'ready' || status === 'submitting') && invite && (
          <form onSubmit={submit} data-testid="invite-form">
            <div className="font-display text-2xl mb-2 flex items-center gap-2"><ShieldCheck size={20} className="text-heritage" /> You&apos;re in!</div>
            <p className="text-muted text-sm mb-6">
              You&apos;re being added as an <strong>Editor admin</strong>.
              Set a password for <strong className="break-all">{invite.email}</strong> and you&apos;ll be signed in.
            </p>

            <label className="block mb-3">
              <span className="text-xs uppercase tracking-wide text-charcoal/60">New password</span>
              <input data-testid="invite-password" type="password" required minLength={10} value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full border border-charcoal/15 rounded-xl px-3 py-2.5 text-sm" />
              <span className="text-[11px] text-charcoal/50">Minimum 10 characters.</span>
            </label>
            <label className="block mb-5">
              <span className="text-xs uppercase tracking-wide text-charcoal/60">Confirm password</span>
              <input data-testid="invite-confirm" type="password" required minLength={10} value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 w-full border border-charcoal/15 rounded-xl px-3 py-2.5 text-sm" />
            </label>

            {errMsg && <div className="text-sm text-heritage mb-3" data-testid="invite-error-msg">{errMsg}</div>}

            <button type="submit" disabled={status === 'submitting'} data-testid="invite-submit"
              className="w-full bg-forest text-base rounded-xl py-3 font-semibold hover:bg-heritage transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
              <Lock size={14} /> {status === 'submitting' ? 'Setting up…' : 'Set password & sign in'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
